import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CostLineCheck,
  CostLineIndexEntry,
} from "@shared-core/data/cost-engine-validation-types";
import {
  costCheckStateLabel,
  costCheckStateTone,
  costHeadLabel,
  formatCheckFeed,
  formatCheckRaisedBy,
  formatCostLineDifferenceTotal,
  formatCostLineLargestDelta,
  formatRouteSourceLine,
  fixedCostOptionsLabel,
} from "@shared-core/domain/cost-engine-validation-presentation";
import { OURS_THARSTERN_LEGEND } from "@shared-core/domain/engine-tharstern-comparison";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import { decodeCostLineId, paths } from "@shared-core/routing/routes";
import { SettingsBreadcrumbs } from "../app/components/patterns/SettingsBreadcrumbs";
import { Badge } from "../app/components/primitives/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import {
  ComparisonCells,
  ComparisonInline,
  ComparisonSubHeaderCells,
} from "../app/components/patterns/ComparisonTriplet";

/** The breadcrumb trail shared by every state of this page — Settings ›
 *  Catalogue › Live costing › Checks, plus this cost line's own identity
 *  once it has loaded. */
function crumbs(leaf?: string) {
  const base = [
    { label: "Settings", to: paths.settingsIntegrations() },
    { label: "Catalogue", to: paths.settingsCatalogues() },
    { label: "Live costing", to: paths.settingsLiveCostingStatus() },
  ];
  return leaf
    ? [
        ...base,
        {
          label: "Checks",
          to: paths.settingsLiveCostingChecks({ view: "cost-line" }),
        },
        { label: leaf },
      ]
    : [...base, { label: "Checks" }];
}

/**
 * The cost-line drill-in, evolved from a side sheet into a full page (review
 * decision, `docs/features/cost-engine-productionisation/02_glossary_and_live_costing.md`):
 * a cost line's own identity (cost centre, cost head), health, money summary,
 * every dictionary item it affects, the checks proving its health (each
 * linking to its cost pair), and the standards row(s) it is expected to
 * follow. Direct-load safe from the URL — fetches everything itself from the
 * `:costLineId` route param.
 */
export function LiveCostingCostLinePage() {
  const adapter = useDataAdapter();
  const { costLineId } = useParams<{ costLineId: string }>();
  // Memoized: decodeCostLineId returns a fresh object each call, and an
  // unstable dependency here would retrigger `load`'s useCallback (and thus
  // the fetch effect below) on every render — never settling.
  const decoded = useMemo(
    () => (costLineId ? decodeCostLineId(costLineId) : null),
    [costLineId],
  );

  const [costLine, setCostLine] = useState<CostLineIndexEntry | null>(null);
  const [checks, setChecks] = useState<CostLineCheck[] | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    if (!decoded) return;
    setLoadError(false);
    Promise.all([
      adapter.getValidationCostLine(decoded.costCentreId, decoded.component),
      adapter.listCostLineChecks(decoded.costCentreId, decoded.component),
    ])
      .then(([entry, checkList]) => {
        if (entry === null) {
          setNotFound(true);
          return;
        }
        setCostLine(entry);
        setChecks(checkList);
      })
      .catch(() => setLoadError(true));
  }, [adapter, decoded]);

  useEffect(() => {
    load();
  }, [load]);

  if (!decoded) {
    return (
      <BreadcrumbsOnly>
        <p className="text-muted-foreground">
          This cost-line link is malformed.
        </p>
      </BreadcrumbsOnly>
    );
  }

  if (loadError) {
    return (
      <BreadcrumbsOnly>
        <p className="text-destructive">Failed to load this cost line.</p>
      </BreadcrumbsOnly>
    );
  }

  if (notFound) {
    return (
      <BreadcrumbsOnly>
        <p className="text-muted-foreground">
          No such cost line. The link may be out of date.
        </p>
      </BreadcrumbsOnly>
    );
  }

  if (!costLine) {
    return (
      <BreadcrumbsOnly>
        <p className="text-muted-foreground">Loading…</p>
      </BreadcrumbsOnly>
    );
  }

  const { moneySummary } = costLine;

  return (
    <div className="space-y-6">
      <SettingsBreadcrumbs crumbs={crumbs(costLine.costLine.label)} />

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold text-foreground">
            {costLine.costLine.label}
          </h1>
          <Badge variant={costCheckStateTone(costLine.health)}>
            {costCheckStateLabel(costLine.health)}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          {costLine.costLine.costCentreCode} ·{" "}
          {costLine.costLine.costCentreName} ·{" "}
          {costHeadLabel(costLine.costLine.costHead)} · affects PT
          {costLine.productTypesAffected.join(", PT")} · {costLine.checkCount}{" "}
          checks, {costLine.missCount} misses
        </p>
        <p className="text-xs text-muted-foreground">{OURS_THARSTERN_LEGEND}</p>
      </div>

      <div className="grid gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm sm:grid-cols-4">
        <ComparisonInline
          value={{
            kind: "money",
            ours: costLine.ourMoneyPence,
            tharstern: costLine.tharsternMoneyPence,
            dashReason: "This cost line has not yet returned a check.",
            toneOverride: costCheckStateTone(costLine.health),
          }}
          label="Total:"
        />
        <div className="flex flex-col gap-0.5">
          <span className="font-medium">Difference (total)</span>
          <span className="text-muted-foreground tabular-nums">
            {moneySummary.differingCheckCount === 0
              ? "—"
              : formatCostLineDifferenceTotal(
                  moneySummary.differenceTotalPence,
                  moneySummary.differingCheckCount,
                )}
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="font-medium">Largest over</span>
          <span className="text-muted-foreground tabular-nums">
            {moneySummary.largestOverPence == null
              ? "—"
              : formatCostLineLargestDelta(
                  moneySummary.largestOverPence,
                  moneySummary.largestOverCount,
                )}
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="font-medium">Largest under</span>
          <span className="text-muted-foreground tabular-nums">
            {moneySummary.largestUnderPence == null
              ? "—"
              : formatCostLineLargestDelta(
                  moneySummary.largestUnderPence,
                  moneySummary.largestUnderCount,
                )}
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Affected dictionary items
        </p>
        {costLine.affectedDictionaryItems.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No dictionary item currently cites this cost line.
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {costLine.affectedDictionaryItems.map((item) => (
              <li key={item.dictionaryItemId}>
                <Link
                  to={paths.dictionaryItemRecord({
                    dictionaryItemId: item.dictionaryItemId,
                  })}
                  className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-sm text-[color:var(--color-primary)] hover:underline"
                >
                  {item.name}
                  <span className="text-xs text-muted-foreground">
                    PT{item.productTypeId}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Standards row{costLine.standardsRows.length === 1 ? "" : "s"} followed
        </p>
        {costLine.standardsRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No standards row recorded for this cost line yet.
          </p>
        ) : (
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {costLine.standardsRows.map((row) => (
              <li key={row}>{row}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Checks
        </p>
        {checks === null ? (
          <p className="text-sm text-muted-foreground">Loading checks…</p>
        ) : checks.length === 0 ? (
          <p className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            No checks recorded for this cost line yet.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Feed</TableHead>
                  <TableHead>Item / variant</TableHead>
                  <TableHead>Quantity</TableHead>
                  <ComparisonSubHeaderCells />
                  <TableHead>Result</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {checks.map((check) => (
                  <TableRow key={check.checkId}>
                    <TableCell className="text-muted-foreground">
                      {formatDateTimeOrDash(check.checkedAt)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <div>{formatCheckFeed(check.feed)}</div>
                      {formatCheckRaisedBy(check.raisedBy, check.orderedAt) && (
                        <div className="text-xs text-muted-foreground">
                          {formatCheckRaisedBy(check.raisedBy, check.orderedAt)}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground">
                        {formatRouteSourceLine(check.routeSource)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Link
                        className="text-[color:var(--color-primary)] hover:underline"
                        to={paths.inHouseSupplierCost({
                          variantId: check.variantId,
                          quantity: check.quantity,
                        })}
                      >
                        {check.dictionaryItemName} · {check.variantLabel}
                      </Link>
                      {(check.fixedCostOptions ?? []).length > 0 && (
                        <div className="text-xs text-muted-foreground">
                          with{" "}
                          {fixedCostOptionsLabel(check.fixedCostOptions ?? [])}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">
                      {check.quantity.toLocaleString()}
                    </TableCell>
                    <ComparisonCells
                      value={{
                        kind: "money",
                        ours: check.ourValuePence,
                        tharstern: check.tharsternValuePence,
                        toneOverride: costCheckStateTone(check.result),
                      }}
                    />
                    <TableCell>
                      <Badge variant={costCheckStateTone(check.result)}>
                        {costCheckStateLabel(check.result)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

function BreadcrumbsOnly({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <SettingsBreadcrumbs crumbs={crumbs()} />
      {children}
    </div>
  );
}
